import type { CatalogCard, ListingResearch, ListingResult, SearchResponse } from "@pokifolio/contracts";
import { useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { toast } from "sonner";

import { Alert } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Select } from "../../components/ui/input";
import { cn } from "../../lib/cn";
import { useCurrentUser } from "../auth/session";
import { CardChooser } from "../research/card-chooser";
import { toListingResearch } from "../research/format";
import { ResearchCell } from "../research/research-cell";
import { useListingResearch, useSaveResearch } from "../research/research-queries";
import {
  MATCH_LABELS,
  MATCH_TONES,
  SOURCE_NAMES,
  WEAR_LABELS,
  describeAvailability,
  formatMoney,
  formatTime,
  isWearWarning,
  listingTypeLabel,
  sourceSummary,
} from "./format";

type MatchFilter = "best" | "all";
type WebsiteFilter = "all" | "ebay" | "other";
type AvailabilityFilter = "all" | "buyable";

interface Filters {
  readonly match: MatchFilter;
  readonly website: WebsiteFilter;
  readonly availability: AvailabilityFilter;
}

/** What a row needs for its Research column (absent for people who may not save research). */
interface RowResearch {
  readonly saved: ListingResearch | undefined;
  readonly searchedCard: CatalogCard | null;
  readonly searchedWords: string;
  readonly currentUserId: string;
}

const COLUMN_COUNT = 5;

function visible(result: ListingResult, { match, website, availability }: Filters): boolean {
  if (match === "best" && result.match.level === "uncertain") return false;
  if (
    availability === "buyable" &&
    (result.availability.status === "sold_out" || result.availability.status === "ended")
  )
    return false;
  if (website === "ebay") return result.website === "eBay";
  if (website === "other") return result.website !== "eBay";
  return true;
}

/** Step 2: what each source said, and every listing found, best match first. */
export function SearchResults({ search }: { search: SearchResponse }) {
  const [match, setMatch] = useState<MatchFilter>("best");
  const [website, setWebsite] = useState<WebsiteFilter>("all");
  const [availability, setAvailability] = useState<AvailabilityFilter>("all");
  const matchId = useId();
  const websiteId = useId();
  const availabilityId = useId();

  const filters: Filters = { match, website, availability };
  const shown = search.results.filter((r) => visible(r, filters));
  const hiddenUncertain =
    match === "best"
      ? search.results.filter((r) => visible(r, { ...filters, match: "all" })).length - shown.length
      : 0;
  const problems = search.sources.filter((s) => s.status !== "ok");
  const anyFailed = search.sources.some((s) => s.status === "failed");
  const searchedAt = search.results[0]?.foundAt;

  const me = useCurrentUser();
  const canSave = me.permissions.includes("research.create");
  const listingIds = useMemo(() => search.results.map((r) => r.id), [search.results]);
  // If this lookup fails, Save buttons still work: the server returns the existing record instead of a second one.
  const inResearch = useListingResearch(listingIds, canSave);
  const savedByListing = new Map((inResearch.data ?? []).map((r) => [r.listingId, r]));
  const researchFor = (result: ListingResult): RowResearch | null =>
    canSave
      ? {
          saved: savedByListing.get(result.id),
          searchedCard: search.card,
          searchedWords: search.query,
          currentUserId: me.user.id,
        }
      : null;

  return (
    <section aria-labelledby="results-heading" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="results-heading" className="text-lg font-semibold text-foreground">
            Results for “{search.query}”
          </h2>
          <ul aria-label="Sources" className="mt-2 flex flex-wrap gap-2">
            {search.sources.map((s) => (
              <li key={s.source}>
                <Badge tone={s.status === "ok" ? "success" : s.status === "failed" ? "danger" : "neutral"}>
                  {sourceSummary(s)}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
        {search.results.length > 0 && (
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor={matchId} className="text-xs font-medium text-muted-foreground">
                Show
              </label>
              <Select
                id={matchId}
                value={match}
                onChange={(e) => setMatch(e.target.value as MatchFilter)}
                className="w-56"
              >
                <option value="best">Exact and likely matches</option>
                <option value="all">All results</option>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={websiteId} className="text-xs font-medium text-muted-foreground">
                Websites
              </label>
              <Select
                id={websiteId}
                value={website}
                onChange={(e) => setWebsite(e.target.value as WebsiteFilter)}
                className="w-44"
              >
                <option value="all">All websites</option>
                <option value="ebay">eBay only</option>
                <option value="other">Other websites</option>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={availabilityId} className="text-xs font-medium text-muted-foreground">
                Availability
              </label>
              <Select
                id={availabilityId}
                value={availability}
                onChange={(e) => setAvailability(e.target.value as AvailabilityFilter)}
                className="w-48"
              >
                <option value="all">All listings</option>
                <option value="buyable">Hide sold and ended</option>
              </Select>
            </div>
          </div>
        )}
      </div>

      {problems.length > 0 && (
        <Alert tone="warning">
          <ul className="flex flex-col gap-1">
            {problems.map((s) => (
              <li key={s.source}>
                <span className="font-medium">{SOURCE_NAMES[s.source]}:</span> {s.message ?? "Not available."}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {search.results.length === 0 ? (
        <Card>
          <p className="text-sm text-muted-foreground">
            No listings found.{" "}
            {anyFailed
              ? "Some sources did not answer, so try again in a moment."
              : "Try fewer words or check the card number."}
          </p>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className={cn("w-full text-left", canSave ? "min-w-[1060px]" : "min-w-[900px]")}>
            <caption className="sr-only">
              Listings found{searchedAt === undefined ? "" : `, checked at ${formatTime(searchedAt)}`}
            </caption>
            <thead className="text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  Listing
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Website
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Price
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Availability
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  Match
                </th>
                {canSave && (
                  <th scope="col" className="px-4 py-3 font-medium">
                    Research
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {shown.map((result) => (
                <ResultRow key={result.id} result={result} research={researchFor(result)} />
              ))}
            </tbody>
          </table>
          {shown.length === 0 && (
            <p className="px-4 pb-4 text-sm text-muted-foreground">No listings match these filters.</p>
          )}
        </Card>
      )}

      {hiddenUncertain > 0 && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>
            {hiddenUncertain === 1
              ? "1 uncertain result is hidden."
              : `${String(hiddenUncertain)} uncertain results are hidden.`}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setMatch("all")}>
            Show all results
          </Button>
        </div>
      )}
      {searchedAt !== undefined && (
        <p className="text-xs text-muted-foreground">
          Prices and availability as of {formatTime(searchedAt)}. Always confirm on the website before buying.
        </p>
      )}
    </section>
  );
}

function ResultRow({ result, research }: { result: ListingResult; research: RowResearch | null }) {
  const type = listingTypeLabel(result.listingType);
  const availability = describeAvailability(result.availability);
  const save = useSaveResearch();
  const navigate = useNavigate();
  const [choosingCard, setChoosingCard] = useState(false);
  const saved =
    research?.saved ?? (save.data === undefined ? undefined : toListingResearch(save.data.research));

  const saveAs = (cardId: string) =>
    save.mutate(
      { listingId: result.id, cardId },
      {
        onSuccess: ({ research: record, created }) => {
          setChoosingCard(false);
          const open = {
            label: "Open",
            onClick: () => void navigate({ to: "/research/$researchId", params: { researchId: record.id } }),
          };
          if (created) toast.success("Saved to Research", { description: result.title, action: open });
          else
            toast.info("Already in Research", { description: `Saved by ${record.owner.name}`, action: open });
        },
      },
    );
  // After a typed-words search the exact card must be chosen first (D-50).
  const startSave = (searchedCard: CatalogCard | null) => {
    if (searchedCard === null) setChoosingCard(true);
    else saveAs(searchedCard.id);
  };
  const cancelChoosing = () => {
    setChoosingCard(false);
    save.reset();
  };

  return (
    <>
      <tr className="border-t border-border align-top">
        <td className="px-4 py-3">
          <div className="flex gap-3">
            {result.imageUrl === null ? (
              <div className="size-14 shrink-0 rounded bg-muted" aria-hidden="true" />
            ) : (
              <img
                src={result.imageUrl}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                className="size-14 shrink-0 rounded object-contain"
              />
            )}
            <div className="flex flex-col gap-1">
              <a
                href={result.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-start gap-1 text-sm font-medium text-primary hover:underline"
              >
                {result.title}
                <ExternalLink className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
              <span className="text-xs text-muted-foreground">
                {[type, result.condition].filter((v) => v !== null).join(" · ")}
              </span>
              {result.wear !== null && (
                <span
                  className={
                    isWearWarning(result.wear)
                      ? "text-xs font-medium text-amber-800 dark:text-amber-300"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {isWearWarning(result.wear) ? "⚠ " : ""}Condition: {WEAR_LABELS[result.wear]}
                </span>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-foreground">{result.website}</td>
        <td className="px-4 py-3 text-sm">
          {result.price === null ? (
            <span className="text-muted-foreground">See website</span>
          ) : (
            <div className="flex flex-col gap-0.5">
              <span className="font-medium text-foreground">
                {result.listingType === "auction" ? "Bid " : ""}
                {formatMoney(result.price)}
              </span>
              {result.priceConfidence === "hint" && (
                <span className="text-xs text-amber-800 dark:text-amber-300">
                  From search results; may be out of date
                </span>
              )}
            </div>
          )}
        </td>
        <td className="px-4 py-3">
          <Badge tone={availability.tone}>{availability.label}</Badge>
          {availability.note !== null && (
            <p className="mt-1 text-xs text-muted-foreground">{availability.note}</p>
          )}
        </td>
        <td className="px-4 py-3">
          <Badge tone={MATCH_TONES[result.match.level]}>{MATCH_LABELS[result.match.level]}</Badge>
          <ul className="mt-1 flex flex-col text-xs text-muted-foreground">
            {result.match.reasons.slice(0, 3).map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </td>
        {research !== null && (
          <td className="px-4 py-3">
            <ResearchCell
              listingTitle={result.title}
              saved={saved}
              currentUserId={research.currentUserId}
              searchedCardId={research.searchedCard?.id ?? null}
              saving={save.isPending && !choosingCard}
              error={choosingCard ? null : save.error}
              onSave={() => startSave(research.searchedCard)}
            />
          </td>
        )}
      </tr>
      {research !== null && choosingCard && saved === undefined && (
        <tr className="bg-accent/40">
          <td colSpan={COLUMN_COUNT + 1} className="px-4 py-4">
            <CardChooser
              listingTitle={result.title}
              initialText={research.searchedWords}
              busy={save.isPending}
              error={save.error}
              onChoose={(card) => saveAs(card.id)}
              onCancel={cancelChoosing}
            />
          </td>
        </tr>
      )}
    </>
  );
}
